import type { EmployeeStatus, EmploymentType } from '../employees/employees.dto.js';
import type {
  GlobalMaterialDto,
  GlobalMaterialPriceVersionRow,
  MaterialOverrideDto,
  MaterialPriceOverrideVersionRow,
} from '../materials-catalog/materials-catalog.dto.js';
import type {
  MaterialCategoryConfigDocument,
  MaterialOrderingConfigDocument,
} from '../materials-presentation/materials-presentation.dto.js';

export const importPackTypeValues = ['identity', 'catalog', 'combined', 'employees'] as const;
export type ImportPackType = (typeof importPackTypeValues)[number];

export interface ImportPackEnvelopeV1 {
  version: 1;
  id: string;
  type: ImportPackType;
  companyId: string;
  locationId: string | null;
  issuedAt: string; // ISO datetime
  payload: unknown;
  publicKeyId: string | null;
  signature: string | null;
}

export interface ImportPackEmployeePayload {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string | null;
  userId: string | null;
  employeeNumber: string | null;
  timezone: string | null;
  firstName: string;
  lastName: string;
  displayName: string;
  status: EmployeeStatus;
  hireDate: string | null; // ISO date (YYYY-MM-DD)
  terminationDate: string | null; // ISO date (YYYY-MM-DD)
  employmentType: EmploymentType | null;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface ImportPackEmployeesPayload {
  employees: ImportPackEmployeePayload[];
}

export interface ImportPackCatalogPayload {
  materials: GlobalMaterialDto[];
  overrides: MaterialOverrideDto[];
  globalPriceVersions: GlobalMaterialPriceVersionRow[];
  overridePriceVersions: MaterialPriceOverrideVersionRow[];
  companyOrdering: MaterialOrderingConfigDocument | null;
  companyCategories: MaterialCategoryConfigDocument | null;
}

export const issueImportPackTypeValues = ['employees', 'catalog'] as const;
export type IssueImportPackType = (typeof issueImportPackTypeValues)[number];

export interface IssueEmployeesImportPackRequest {
  companyId: string;
  type: 'employees';
  storeId: string;
  locationId: string | null;
}

export interface IssueCatalogImportPackRequest {
  companyId: string;
  type: 'catalog';
  storeId: string;
  locationId: null;
}

export type IssueImportPackRequest =
  | IssueEmployeesImportPackRequest
  | IssueCatalogImportPackRequest;

export interface IssueImportPackSummary {
  type: IssueImportPackType;
  employeeCount: number;
  materialCount: number;
  overrideCount: number;
  globalPriceVersionCount: number;
  overridePriceVersionCount: number;
  hasCompanyOrdering: boolean;
  hasCompanyCategories: boolean;
}

export interface IssueImportPackResponse {
  envelope: ImportPackEnvelopeV1;
  summary: IssueImportPackSummary;
}

export type ApplyImportPackRequest = ImportPackEnvelopeV1;

export const importPackApplyStatusValues = ['applied', 'replayed'] as const;
export type ImportPackApplyStatus = (typeof importPackApplyStatusValues)[number];

export interface ApplyImportPackResponse {
  packId: string;
  status: ImportPackApplyStatus;
  appliedAt: string | null;
  appliedCount: number;
  rejectedCount: number;
  warnings: string[];
}
